import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText
} from '@mui/material';
import { useSelector } from 'react-redux';
import useStyles from './style';
import { styled } from '@mui/material/styles';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmojiPicker from 'emoji-picker-react';
import Message from '../Message/message';
import { SocketContext } from '../../contexts/SocketContextProvider';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import { executeBBCard } from '../../utils/bbCardService';
import axios from 'axios';
import { selectConfig } from '../../redux/slices/configSlice';


const WAITING_FOR_AGENT = 'WAITING_FOR_AGENT';
const PARTICIPANT_ADDED = 'PARTICIPANT_ADDED';
const PARTICIPANT_DISCONNECTED = 'PARTICIPANT_DISCONNECTED';
const SYSTEM_CONNECTED = 'SYSTEM_CONNECTED';
const SUPERVISOR = 'SUPERVISOR';
const AGENT = 'AGENT';

// Add props interface
interface ChatProps {
  mParam?: string | null;
}

const Chat: React.FC<ChatProps> = ({ mParam }) => {
  const [msg, setMsg] = useState('');
  const [isEmojiShown, setIsEmojiShown] = useState(false);
  const styles = useStyles();
  const [chatConnectivityStatus, setChatConnectivityStatus] = useState(WAITING_FOR_AGENT);
  const [agentDisplayName, setAgentDisplayName] = useState('');
  const [disableChat, setDisableChat] = useState('disabled'); // Placeholder for disabling chat
  const [repliedRichMediaIds, setRepliedRichMediaIds] = useState<Set<string>>(new Set()); // Track replied rich media message IDs

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogType, setDialogType] = useState<'success' | 'error'>('success');
  
  const textarea = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const { user } = useSelector((state: any) => state);

  const config = useSelector(selectConfig);
  const formData = config?.data?.[0]?.attributes;

  type SocketContextProps = {
    messages: any[];
    participants: any[];
    isSocketConnected: boolean;
    isBotConnected: boolean;
    engagementParam: any;
    socket: any;
  };

  const socketContext = useContext(SocketContext) as SocketContextProps | undefined;

  const {
    messages = [],
    participants = [],
    isSocketConnected = false,
    isBotConnected = false,
    engagementParam = {},
    socket = {}
  } = socketContext || {};

  let isWaiting = false;

  const handleEmojiSelect = (emoji: any) => {
    if (textarea.current) {
      const el = textarea.current;
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;
      const updatedText = el.value.slice(0, start) + emoji.emoji + el.value.slice(end);
      setMsg(updatedText);

      // Update cursor position
      el.setSelectionRange(start + emoji.emoji.length, start + emoji.emoji.length);
      el.focus();
    } else {
      setMsg((prev) => prev + emoji.emoji);
    }
  };

  function systemMessageCheck(lang: string): string {
    switch (lang) {
      case 'EN':
      case 'IN':
      case 'HI':
      case 'TE':
        return 'Please stay online while we connect you to our Online Support Representative.';
  
      case 'KR':
        return '저희 사이트를 방문해주셔서 감사합니다! 상담원과 연결중이오니 잠시만 기다려주시기 바랍니다.';
  
      case 'ID':
        return 'Mohon tunggu, kami sambungkan Anda pada salah satu agen representatif kami.';
  
      case 'TH':
        return 'ขออภัย ขณะนี้เจ้าหน้าที่ให้บริการเต็มทุกคู่สาย รบกวนรอสักครู่';
  
      case 'CN':
        return '目前客服人员繁忙，关于给您造成长时间的等待我们深表歉意。请您耐心等待，客服人员将立即为您服务。';
  
      default:
        return 'Please stay online while we connect you to our Online Support Representative.';
    }
  }
    
  let languageCheck = engagementParam?.language;
  if (languageCheck) {  
      if (languageCheck?.includes('ISTG')) {
          languageCheck = languageCheck.replace('ISTG', '');
      }

      console.log(languageCheck, 'languageCheck');
      let waitingSystemMsg = systemMessageCheck(languageCheck?.replace(/ /g,''))

      if (!isWaiting) {
        isWaiting = messages[messages.length - 1]?.body?.elementText.text.includes(waitingSystemMsg);
      }
  }

  useEffect(() => {
    if (user.user) {
      document.body.style.overflow = 'hidden';
      window.scrollTo(0, 0);
      _defaultSendMessage();
      return () => {
        // clean-up function for unmounting
      };
    }
  }, [socketContext?.socket]);

  useEffect(() => {
    if (Object.keys(engagementParam || {}).length > 0 && user.user?.bbc_id) {
      executeBBCard(engagementParam);
    }
  }, [engagementParam, user.user?.bbc_id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isEmojiShown) {
          setIsEmojiShown(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEmojiShown]);


  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]); 

  useEffect(() => {
    const participantsTypes = participants.map(p => p?.participantType);
    const agentNames = participants
      .filter(p => p?.participantType === AGENT)
      .map(p => p?.displayName);
    setAgentDisplayName(agentNames.join(', '));

    if (participantsTypes.includes(AGENT) || participantsTypes.includes(SUPERVISOR)) {
      
      setChatConnectivityStatus(PARTICIPANT_ADDED);
      setDisableChat('');
      isWaiting = false;
    } else if (messages.length > 0) {
      setChatConnectivityStatus(PARTICIPANT_DISCONNECTED);
    }
  }, [participants]);

  useEffect(() => {
    if (isSocketConnected && isBotConnected) {
      setChatConnectivityStatus(SYSTEM_CONNECTED);
      setDisableChat('');
    }
  }, [isBotConnected]);

  useEffect(()=>{
      if (isWaiting) {
      setDisableChat("disabled");
      }
  },[isWaiting])

  const getProviderIds = () => {
    const isVerified = user?.user?.engagementParameters?.CustomerVerified === "true";
  
    const providerDialogId = isVerified
      ? user?.user?.chProviderDialogId ?? user?.user?.providerDialogId
      : user?.user?.providerDialogId;
  
    const providerSenderId = isVerified
      ? user?.user?.chProviderSenderId ?? user?.user?.providerSenderId
      : user?.user?.providerSenderId;
  
    return { providerDialogId, providerSenderId };
  };

  const _sendMessage = () => {
    if (!msg.trim()) return;
    const { providerDialogId, providerSenderId } = getProviderIds();
    
    const message = {
      body: {
        elementType: 'text',
        elementText: {
          text: msg,
          textFormat: 'PLAINTEXT',
        },
      },
      senderName: user.user.displayName,
      providerDialogId,
      providerSenderId,
      engagementParam: user.user.engagementParameters,
      customerIdentifiers: user.user.customerIdentifiers,
    };

    setMsg('');

    console.log("Socket context:", socketContext);
    if (socketContext?.socket) {

      socketContext?.socket.emit('message', message, (res: any) => {
        setIsEmojiShown(false)
        if (res.hasError) {
          console.error('Message error:', res.error);
        }
      });
    }
  };

  const _defaultSendMessage = () => {
    console.log(user.user);
    const { providerDialogId, providerSenderId } = getProviderIds();

    // Use mParam as the default message if available
    const defaultMessage = mParam || formData?.ChatBox?.HelloMessageText || 'Hello';

    const message = {
      body: {
        elementType: 'text',
        elementText: {
          text: defaultMessage,
          textFormat: 'PLAINTEXT',
        },
      },
      senderName: user.user.displayName,
      providerDialogId,
      providerSenderId,
      engagementParam: user.user.engagementParameters,
      customerIdentifiers: user.user.customerIdentifiers,
    };
    
    if (socketContext?.socket) {
      socketContext?.socket.emit('message', message, (res: any) => {
        if (res.hasError) {
          console.error('Message error:', res.error);
        }
      });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileObj = event.target.files?.[0];
    if (!fileObj) return;
    const { providerDialogId, providerSenderId } = getProviderIds();

    const allowedFileTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedFileTypes.includes(fileObj.type)) {
      setDialogMessage(formData?.ChatBox?.FileTypeErrorMessage);
      setDialogType('error');
      setIsDialogOpen(true);
      return;
    }

    if (fileObj.size > 4000000) {
      setDialogMessage(formData?.ChatBox?.FileSizeErrorMessage);
      setDialogType('error');
      setIsDialogOpen(true);
      return;
    }

    console.log(user);
    // Prepare and send file to server
    const formDetails = new FormData();
    formDetails.append('image', fileObj);
    formDetails.append('providerDialogId', providerDialogId);
    formDetails.append('providerSenderId', providerSenderId);
    formDetails.append('engagementParam', JSON.stringify(user?.user?.engagementParameters));
    formDetails.append('customerIdentifiers', JSON.stringify(user?.user?.customerIdentifiers));

    axios.post(`${process.env.REACT_APP_CHATCON_URL}/image-upload`, formDetails)
      .then(() => {
        setDialogMessage(formData?.ChatBox?.FileUploadSuccessMessage ?? 'File uploaded successfully.');
        setDialogType('success');
        setIsDialogOpen(true);
      })
      .catch(() => {
        setDialogMessage(formData?.ChatBox?.FileUploadFaileMessage ?? 'Failed to upload the file. Please try again.');
        setDialogType('error');
        setIsDialogOpen(true);
      });
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const restartChat = () => {
      setChatConnectivityStatus(WAITING_FOR_AGENT);
      _defaultSendMessage();
  };

  // Define a type for the message object
  type ChatMessage = {
    senderParticipantType: string;
    body: {
      richMediaPayload?: any;
      elementText?: {
        text: string;
      };
      elementType?: string;
    };
    messageId?: string;
    // Add other properties as needed
  };

  // Add this function to check if a new message is a reply to rich media
  const checkForRichMediaReply = useCallback((newMessage: ChatMessage) => {
    if (newMessage.senderParticipantType === "CUSTOMER") {
      // Find the most recent rich media message before this customer message
      const messageIndex = messages.findIndex(msg => msg === newMessage);
      if (messageIndex > 0) {
        // Look back up to 3 messages to find the rich media that was replied to
        for (let i = 1; i <= 3; i++) {
          if (messageIndex - i >= 0) {
            const prevMsg = messages[messageIndex - i];
            if (prevMsg && 
                prevMsg.senderParticipantType === "SYSTEM" && 
                prevMsg.body.richMediaPayload) {
              // Found the rich media message that was replied to
              if (prevMsg.messageId) {
                setRepliedRichMediaIds(prev => {
                  const newSet = new Set(prev);
                  newSet.add(prevMsg.messageId!);
                  return newSet;
                });
              }
              break;
            }
          }
        }
      }
    }
  }, [messages]);

  // Add this effect to check new messages
  useEffect(() => {
    if (messages.length > 0) {
        const latestMessage = messages[messages.length - 1];
        checkForRichMediaReply(latestMessage);
    }
  }, [messages, checkForRichMediaReply]);

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {/* Display chat status */ }
        
        {chatConnectivityStatus === "WAITING_FOR_AGENT" && (
          <Typography sx={{ background: "#FF875A", color: "#fff", display: "flex", alignItems: "center", padding: "8px" }} mt={0.5}>
            <CircularProgress size={20} thickness={4} sx={{ color: "#fff", marginRight: "8px" }} />
            {formData?.ChatLoader?.WaitingForAgentText}
          </Typography>
        )}
        {chatConnectivityStatus === "SYSTEM_CONNECTED" && (
          <Typography sx={{ background: "#1ED760", color: "#fff", display: "flex", alignItems: "center", padding: "8px" }} mt={0.5}>
            <HeadsetMicIcon sx={{ marginRight: "8px" }} />
            {formData?.ChatLoader?.AgentConnectedText}
          </Typography>
        )}
        {chatConnectivityStatus === "PARTICIPANT_ADDED" && (
          <Typography sx={{ background: "#1CD760", color: "#fff", display: "flex", alignItems: "center", padding: "8px" }} mt={0.5}>
            <HeadsetMicIcon sx={{ marginRight: "8px" }} /> {formData?.ChatBox?.YouAreChattingWith} { agentDisplayName }
          </Typography>
        )}
        {chatConnectivityStatus === "PARTICIPANT_DISCONNECTED" && (
          <Typography sx={{ background: "#5E5E5E", color: "#fff", display: "flex", alignItems: "center", padding: "8px" }} mt={0.5}>
            <CloseIcon sx={{ marginRight: "8px" }} /> {formData?.ChatLoader?.SessionEndText}
          </Typography>
        )}
      </Box>

      {/* Message display area */}
      <Box sx={{ height: "calc(100% - 205px)", overflowY: "auto", position: "absolute", width: "100%" }} ref={scrollRef}>
        {messages.map((item, index) => {
          // Check if any of the previous 3 messages were rich media
          let isPrevRichMedia = false;
          let prevRichMediaId = "";
          
          // Look back up to 3 messages
          for (let i = 1; i <= 3; i++) {
            if (index - i >= 0) {
              const prevMsg = messages[index - i];
              if (prevMsg && 
                  prevMsg.senderParticipantType === "SYSTEM" && 
                  prevMsg.body.richMediaPayload) {
                isPrevRichMedia = true;
                prevRichMediaId = prevMsg.messageId || "";
                break;
              }
            }
          }
          
          // Check if this specific rich media has been replied to
          const isThisRichMediaReplied = item.messageId ? repliedRichMediaIds.has(item.messageId) : false;
          
          return <Message 
                   message={item} 
                   key={item.messageId || index} 
                   previousMessageType={isPrevRichMedia ? "RICH_MEDIA" : "NORMAL"}
                   prevRichMediaId={prevRichMediaId}
                   isThisRichMediaReplied={isThisRichMediaReplied}
                 />;
        })}
      </Box>

      {/* Input box and buttons */}
      <Box sx={{ position: "absolute", bottom: "10px", padding: "10px", display: "flex", width: "100%" }}>
          { (chatConnectivityStatus != "PARTICIPANT_DISCONNECTED" && isSocketConnected)?
            <Box sx={{ display: "flex", position: "relative", width: "100%" }}>
              {/* Emoji Picker */}
              <IconButton
                disabled={disableChat === "disabled"}
                aria-label="emoji"
                onClick={() => setIsEmojiShown((prev) => !prev)}
                sx={{ 
                  color: 'primary.main',
                  position: "absolute",
                  top: "8px",
                  left: "5px",
                  zIndex: "111111",
                }}
              >
                <EmojiEmotionsIcon />
              </IconButton>
              {isEmojiShown && (
                <Box sx={{ position: 'absolute', bottom: '60px', left: '10px', zIndex: 2 }}>
                  <EmojiPicker onEmojiClick={handleEmojiSelect} />
                </Box>
              )}
              <TextField
                disabled={disableChat === "disabled"}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Text Message"
                variant="outlined"
                sx={{
                  borderRadius: "50px",
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "50px",
                    padding: "0 20px 0 30px"
                  },
                  '& .MuiOutlinedInput-input': {
                    width: "calc(100% - 90px)", // Ensuring input itself respects the width constraint
                  },
                  backgroundColor: '#E5E5EB',
                  '& .Mui-focused': {
                    backgroundColor: '#fff',
                  },
                  
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    _sendMessage();
                  }
                }}
              />
              <IconButton aria-label="send" onClick={_sendMessage} disabled={disableChat === "disabled"}
                sx={{ 
                  color: 'primary.main',
                  position: "absolute",
                  top: "2px",
                  right: "6px",
                  zIndex: "111111",
                }}
              >
                <SendIcon sx={{ color: disableChat === "disabled" ? "#e5e5eb" : "primary.main", fontSize: "36px" }} />
              </IconButton>
              {formData?.ChatBox?.EnableAttachment && disableChat !== "disabled" ?
                <Button
                disabled={disableChat === "disabled"}
                  sx={{
                    padding: 0,
                    minWidth: "auto",
                    '& .MuiButton-startIcon > *:nth-of-type(1)': {
                      fontSize: '30px',
                    },
                    position: "absolute",
                    top: "11px",
                    right: "51px",
                    zIndex: "111111",
                  }}
                  component="label"
                  variant="text"
                  startIcon={<AttachFileIcon sx={{ fontSize: "36px", transform: "rotate(45deg)" }} />}
                >
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    onChange={(e) => handleFileChange(e)}
                  />
                </Button> : null
              }

              {/* Dialog for Success/Error Messages */}
              <Dialog
                open={isDialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{
                  textAlign: "center",
                  "& .MuiPaper-root": {
                    borderRadius: "40px",
                    padding: "20px 10px",
                  }
                }}
              >
                <DialogTitle id="alert-dialog-title">
                  <Box sx={{ background: dialogType === 'success' ? "#28A745" : "#FF0000", borderRadius: "50%", padding: "10px", display: "inline-block" }}>
                    {dialogType === 'success' ? (
                      <CheckIcon sx={{ fontSize: "60px", color: "#FFF" }} className={styles.iconCircle} />
                    ) : (
                      <CloseIcon sx={{ fontSize: "60px", color: "#FFF" }} className={styles.iconCircle} />
                    )}
                  </Box>
                  {dialogType === 'success' ? (
                    <Typography className={styles.iconText} sx={{ color: "#28A745" }}>{ formData?.ChatBox?.FileUploadSuccessTitle ??  "SUCCESS" }</Typography>
                  ) : (
                    <Typography className={styles.iconText} sx={{ color: "#FF0000" }}>{ formData?.ChatBox?.FileUploadFaileTitle ??  "Error" }</Typography>
                  )}

                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    {dialogMessage}
                  </DialogContentText>
                </DialogContent>
              </Dialog>
            </Box> : isSocketConnected ? (
            <Box sx={{ textAlign: "center", marginTop: 2, width: "100%" }}>
              <Button
                className='restartSession'
                variant="contained"
                color="primary"
                onClick={restartChat}
                sx={{
                  borderRadius: "20px",
                  padding: "10px 20px",
                  fontSize: "16px",
                  textTransform: "none",
                }}
              >
                {formData?.ReconnectChat?.StartNewSessionLabel}
              </Button>
            </Box>
          ) : null }
      </Box>
    </>
  );
}

export default Chat;
